import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  loginWithPassword,
  logoutSession,
  refreshSession,
  verifyLoginMfa,
  type ApiSurface,
} from './login.js';
import { getAccessToken, getTokenExpiresAt, subscribeAuthState } from './session.js';
import {
  isIdentitySessionError,
  shouldAttemptTokenRefresh,
  shouldForceSignIn,
  subscribeSessionErrors,
} from './sessionErrors.js';
import { SIGN_IN_PATHS } from './routes.js';

function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env;
    if (env?.VITE_API_BASE_URL) return env.VITE_API_BASE_URL;
  }
  return '';
}

const REFRESH_LEAD_MS = 60_000;

interface AuthContextValue {
  surface: ApiSurface;
  signInPath: string;
  accessToken: string | null;
  authenticated: boolean;
  bootstrapping: boolean;
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  handleSessionError: (code: string | undefined) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  surface: ApiSurface;
  signInPath?: string;
  children: ReactNode;
}

export function AuthProvider({ surface, signInPath, children }: AuthProviderProps) {
  const resolvedSignInPath = signInPath ?? SIGN_IN_PATHS[surface];
  const baseUrl = getApiBaseUrl();
  const queryClient = useQueryClient();
  const [accessToken, setAccessTokenState] = useState<string | null>(() => getAccessToken());
  const [bootstrapping, setBootstrapping] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRecovery = useRef<Promise<void> | null>(null);

  const scheduleRefresh = useCallback(
    (token: string | null) => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
      if (!token) return;

      const expiresAt = getTokenExpiresAt();
      if (!expiresAt) return;

      const delay = expiresAt - Date.now() - REFRESH_LEAD_MS;
      if (delay <= 0) {
        void refreshSession(baseUrl, surface).then((next) => scheduleRefresh(next));
        return;
      }
      refreshTimer.current = setTimeout(() => {
        void refreshSession(baseUrl, surface).then((next) => scheduleRefresh(next));
      }, delay);
    },
    [baseUrl, surface],
  );

  useEffect(() => {
    return subscribeAuthState(() => {
      const token = getAccessToken();
      setAccessTokenState(token);
      scheduleRefresh(token);
    });
  }, [scheduleRefresh]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh(getAccessToken());
      }
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
  }, [scheduleRefresh]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (getAccessToken()) {
        scheduleRefresh(getAccessToken());
        if (!cancelled) setBootstrapping(false);
        return;
      }
      const token = await refreshSession(baseUrl, surface);
      if (!cancelled) {
        setAccessTokenState(token);
        scheduleRefresh(token);
        setBootstrapping(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [baseUrl, surface, scheduleRefresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await loginWithPassword(baseUrl, surface, email, password);
      setAccessTokenState(result.accessToken);
      scheduleRefresh(result.accessToken);
    },
    [baseUrl, surface, scheduleRefresh],
  );

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string) => {
      const result = await verifyLoginMfa(baseUrl, surface, challengeToken, code);
      setAccessTokenState(result.accessToken);
      scheduleRefresh(result.accessToken);
    },
    [baseUrl, surface, scheduleRefresh],
  );

  const logout = useCallback(async () => {
    await logoutSession(baseUrl, surface);
    setAccessTokenState(null);
    scheduleRefresh(null);
  }, [baseUrl, surface, scheduleRefresh]);

  const refresh = useCallback(async () => {
    const token = await refreshSession(baseUrl, surface);
    setAccessTokenState(token);
    scheduleRefresh(token);
    return token;
  }, [baseUrl, surface, scheduleRefresh]);

  const handleSessionError = useCallback(
    async (code: string | undefined) => {
      if (!isIdentitySessionError(code)) return false;

      const refreshFirst = shouldAttemptTokenRefresh(code);
      if (refreshFirst) {
        const token = await refreshSession(baseUrl, surface);
        if (token) {
          setAccessTokenState(token);
          scheduleRefresh(token);
          return true;
        }
      }

      if (refreshFirst || shouldForceSignIn(code)) {
        await logoutSession(baseUrl, surface);
        setAccessTokenState(null);
        scheduleRefresh(null);
      }

      return false;
    },
    [baseUrl, surface, scheduleRefresh],
  );

  useEffect(
    () =>
      subscribeSessionErrors((code) => {
        if (sessionRecovery.current) return;
        sessionRecovery.current = (async () => {
          const recovered = await handleSessionError(code);
          if (recovered) {
            await queryClient.refetchQueries({ type: 'active' });
            return;
          }
          if (typeof window === 'undefined') return;
          if (window.location.pathname === resolvedSignInPath) return;

          const destination = new URL(resolvedSignInPath, window.location.origin);
          destination.searchParams.set(
            'returnTo',
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
          );
          destination.searchParams.set('reason', 'session-expired');
          window.location.replace(destination.toString());
        })().finally(() => {
          sessionRecovery.current = null;
        });
      }),
    [handleSessionError, queryClient, resolvedSignInPath],
  );

  const value = useMemo(
    () => ({
      surface,
      signInPath: resolvedSignInPath,
      accessToken,
      authenticated: Boolean(accessToken),
      bootstrapping,
      login,
      verifyMfa,
      logout,
      refresh,
      handleSessionError,
    }),
    [
      surface,
      resolvedSignInPath,
      accessToken,
      bootstrapping,
      login,
      verifyMfa,
      logout,
      refresh,
      handleSessionError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
